import { ABP, eLayoutType, PermissionService } from '@abp/ng.core';
import { inject, Injectable } from '@angular/core';
import { Route, Routes } from '@angular/router';
import { MenuItemCategory } from '../interfaces/menu-items';
import { MenuTransformService } from './menu-transform.service';

export interface MenuRouteModule {
  path: string;
  routes: Routes;
}

interface MenuRouteItem {
  route: Route;
  modulePath: string;
}

@Injectable({
  providedIn: 'root',
})
export class MenuGeneratorService {
  private readonly permission = inject(PermissionService);
  private readonly menuTransform = inject(MenuTransformService);

  buildAbpRoutes(modules: MenuRouteModule[]): ABP.Route[] {
    const categories = this.getVisibleMenuCategories(modules);
    const generatedRoutes: ABP.Route[] = [];
    const routeNames = new Map<string, number>();

    categories.forEach((category, categoryIndex) => {
      if (!this.hasVisibleChildren(category)) {
        return;
      }

      const categoryOrder = (categoryIndex + 1) * 10;
      const categoryName = createUniqueRouteName(routeNames, category.title);

      generatedRoutes.push({
        path: '',
        name: categoryName,
        order: categoryOrder,
        invisible: false,
        layout: eLayoutType.application,
      });

      category.subcategories.forEach((subcategory, subcategoryIndex) => {
        const visibleLinks = subcategory.links.filter(
          link => this.permission.getGrantedPolicy(link.requiredPolicy)
        );

        if (!visibleLinks.length) {
          return;
        }

        const subcategoryOrder = categoryOrder + (subcategoryIndex + 1) * 100;
        const subcategoryName = subcategory.title
          ? createUniqueRouteName(routeNames, subcategory.title)
          : undefined;
        const parentName = subcategoryName || categoryName;

        if (subcategoryName) {
          generatedRoutes.push({
            path: '',
            name: subcategoryName,
            parentName: categoryName,
            order: subcategoryOrder,
            invisible: false,
          });
        }

        visibleLinks.forEach((link, linkIndex) => {
          generatedRoutes.push({
            path: link.url,
            name: createUniqueRouteName(routeNames, link.title),
            parentName,
            order: subcategoryOrder + linkIndex + 1,
            layout: eLayoutType.application,
            requiredPolicy: link.requiredPolicy,
          });
        });
      });
    });

    return generatedRoutes;
  }

  getVisibleMenuCategories(modules: MenuRouteModule[]): MenuItemCategory[] {
    return this.menuTransform
      .sortByTranslatedTitles(this.buildMenuCategories(modules))
      .map(category => ({
        ...category,
        subcategories: category.subcategories
          .map(subcategory => ({
            ...subcategory,
            links: subcategory.links.filter(
              link => this.permission.getGrantedPolicy(link.requiredPolicy)
            ),
          }))
          .filter(subcategory => subcategory.links.length > 0),
      }))
      .filter(category => category.subcategories.length > 0);
  }

  private buildMenuCategories(modules: MenuRouteModule[]): MenuItemCategory[] {
    const routesWithMenu = this.collectRoutesWithMenu(modules);
    const categoryMap = new Map<string, Map<string, MenuRouteItem[]>>();

    routesWithMenu.forEach(item => {
      const category = item.route.data?.['menuCategory'] || 'LanguageService::Other';
      const subcategory = item.route.data?.['menuSubcategory'] || '';

      if (!categoryMap.has(category)) {
        categoryMap.set(category, new Map());
      }

      const subcategoryMap = categoryMap.get(category)!;
      if (!subcategoryMap.has(subcategory)) {
        subcategoryMap.set(subcategory, []);
      }

      subcategoryMap.get(subcategory)!.push(item);
    });

    return [...categoryMap.entries()].map(([categoryTitle, subcategoryMap]) => ({
      title: categoryTitle,
      subcategories: [...subcategoryMap.entries()].map(([subcategoryTitle, items]) => ({
        title: subcategoryTitle || undefined,
        links: items.map(item => ({
          title: item.route.data?.['menuTitle'],
          url: `/${item.modulePath}/${item.route.path}`,
          requiredPolicy: item.route.data?.['requiredPolicy'],
        })),
      })),
    }));
  }

  private collectRoutesWithMenu(modules: MenuRouteModule[]): MenuRouteItem[] {
    const menuRoutes: MenuRouteItem[] = [];

    modules.forEach(module => {
      module.routes.forEach(route => {
        if (route.data?.['showInMenu'] === true && route.path) {
          menuRoutes.push({
            route,
            modulePath: module.path,
          });
        }
      });
    });

    return menuRoutes;
  }
  private hasVisibleChildren(category: MenuItemCategory): boolean {
    return category.subcategories.some(subcategory =>
      subcategory.links.some(
        link => this.permission.getGrantedPolicy(link.requiredPolicy)
      )
    );
  }
}

function createUniqueRouteName(routeNames: Map<string, number>, label = ''): string {
  const baseLabel = label;
  const currentCount = routeNames.get(baseLabel) || 0;

  routeNames.set(baseLabel, currentCount + 1);

  if (currentCount === 0) {
    return baseLabel;
  }

  return `${baseLabel}${'\u200B'.repeat(currentCount)}`;
}
